import { API_CONFIG, getAgentFlowHeaders, getAgentFlowUrl } from '../config/api';
import { generateSessionId, generateOutletSessionId } from '../utils/session';

// Types for outlet information
interface OutletInfo {
  outlet_id: string;
  name: string;
  outlet_type: string;
  region: string;
  contact_person: string;
  phone: string;
  city_tier: string;
  store_size: string;
  customer_volume: string;
  operating_hours: string;
  customer_type: string;
  scoring: {
    erfm_score: number;
    mission_score: number;
    order_score: number;
    performance_score: number;
    overall_score: number;
    score_tier: string;
  };
}

// Function to format outlet information for Agent AI
export const formatOutletInfo = (outlet: OutletInfo): string => {
  const {
    outlet_id,
    name,
    outlet_type,
    region,
    contact_person,
    phone,
    city_tier,
    store_size,
    customer_volume,
    operating_hours,
    customer_type,
    scoring
  } = outlet;

  return [
    `outlet_id: ${outlet_id}`,
    `outlet_name: ${name}`,
    `outlet_type: ${outlet_type}`,
    `outlet_tier: ${scoring.score_tier}`,
    `region: ${region} - ${city_tier}`,
    `contact_name: ${contact_person}`,
    `contact_phone: ${phone}`,
    `store_size: ${store_size}`,
    `customer_volume: ${customer_volume}`,
    `operating_hours: ${operating_hours}`,
    `customer_type: ${customer_type}`,
    `performance_score: ${scoring.overall_score}/100`
  ].join(' | ');
};

// Types
export interface AgentFlowMessage {
  question: string;
  overrideConfig?: {
    sessionId?: string;
    state?: {
      outletInfo?: string;
    };
    [key: string]: any;
  };
}

export interface AgentFlowResponse {
  text?: string;
  error?: string;
  [key: string]: any;
}

// AgentFlow Session Class
export class AgentFlowSession {
  private flowId: string;
  private sessionId: string;
  private baseUrl: string;
  private url: string;

  constructor(
    flowId: string = API_CONFIG.AGENTFLOW.FLOW_ID,
    sessionId: string = '',
    baseUrl: string = API_CONFIG.AGENTFLOW.BASE_URL
  ) {
    this.flowId = flowId;
    this.sessionId = sessionId || this.generateSessionId();
    this.baseUrl = baseUrl;
    this.url = getAgentFlowUrl();
    
  }

  // Generate unique session ID
  private generateSessionId(): string {
    return generateSessionId();
  }

  // Send message to AgentFlow AI
  async sendMessage(question: string, additionalConfig?: any): Promise<AgentFlowResponse | null> {
    const payload: AgentFlowMessage = {
      question,
      overrideConfig: {
        sessionId: this.sessionId,
        state: {
          outletInfo: additionalConfig?.outletInfo || ''
        }
      }
    };


    try {
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      const response = await fetch(this.url, {
        method: 'POST',
        headers: getAgentFlowHeaders(),
        body: JSON.stringify(payload),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);


      if (!response.ok) {
        // Handle rate limiting (429) gracefully
        if (response.status === 429) {
          console.warn('⚠️ Rate limit exceeded, skipping API call');
          throw new Error('Rate limit exceeded - please try again later');
        }
        
        let errorMessage = `HTTP error! status: ${response.status}`;
        
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
          console.error('❌ AgentFlow API Error Response:', errorData);
        } catch {
          errorMessage = response.statusText || errorMessage;
          console.error('❌ AgentFlow API Error (non-JSON):', response.statusText);
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      return data;
    } catch (error) {
      // Handle timeout errors
      if (error instanceof Error && error.name === 'AbortError') {
        return null;
      }
      
      return null;
    }
  }

  // Send message with outlet context
  async sendMessageWithContext(
    question: string, 
    outletId: string, 
    outletInfo?: OutletInfo
  ): Promise<AgentFlowResponse | null> {
    const contextConfig = {
      outletInfo: outletInfo ? formatOutletInfo(outletInfo) : ''
    };

    return this.sendMessage(question, contextConfig);
  }

  // Get session ID
  getSessionId(): string {
    return this.sessionId;
  }

  // Reset session
  resetSession(): void {
    this.sessionId = this.generateSessionId();
  }
}

// AgentFlow API Service Class
export class AgentFlowApiService {
  private baseUrl: string;
  private flowId: string;
  private apiKey: string;
  private currentSession: AgentFlowSession | null = null;

  constructor() {
    this.baseUrl = API_CONFIG.AGENTFLOW.BASE_URL;
    this.flowId = API_CONFIG.AGENTFLOW.FLOW_ID;
    this.apiKey = API_CONFIG.AGENTFLOW.API_KEY;
  }

  // Create new session
  createSession(sessionId?: string): AgentFlowSession {
    return new AgentFlowSession(this.flowId, sessionId, this.baseUrl);
  }

  // Create session with outlet ID (for production use)
  createSessionWithOutlet(outletId: string): AgentFlowSession {
    // Use outletId as session_id for consistency with AgentFlow API
    const sessionId = generateOutletSessionId(outletId);
    return new AgentFlowSession(this.flowId, sessionId, this.baseUrl);
  }

  // Get or create current session
  getCurrentSession(): AgentFlowSession {
    if (!this.currentSession) {
      this.currentSession = this.createSession();
    }
    return this.currentSession;
  }

  // Reset current session
  resetCurrentSession(): void {
    this.currentSession = null;
  }

  // Quick send message without session management
  async sendMessage(question: string): Promise<AgentFlowResponse | null> {
    const session = this.createSession();
    return session.sendMessage(question);
  }

  // Send message with current session (persistent)
  async sendMessageWithSession(question: string): Promise<AgentFlowResponse | null> {
    const session = this.getCurrentSession();
    return session.sendMessage(question);
  }

  // Send message with outlet context using outlet-specific session
  async sendMessageWithOutlet(
    question: string, 
    outletId: string, 
    outletInfo?: OutletInfo
  ): Promise<AgentFlowResponse | null> {
    // Create session with outletId for consistency
    const session = this.createSessionWithOutlet(outletId);
    return session.sendMessageWithContext(question, outletId, outletInfo);
  }

  // Get current session ID
  getCurrentSessionId(): string | null {
    return this.currentSession?.getSessionId() || null;
  }
}

// Export singleton instance
export const agentFlowApi = new AgentFlowApiService();
